/**
 * Shared Public Footer
 *
 * Used across all public-facing pages (Landing, How It Works, Cards, FAQ).
 * Shows tagline, product note, Shop product links, Follow us social links,
 * legal/privacy links, and copyright.
 *
 * Legal links (Terms, Privacy) open in-page modals via the LegalModal component.
 * The "Privacy Choices" link opens the consent preferences modal.
 *
 * Product links reuse SHOP_NAV_ITEMS so footer and nav stay in sync (PBI #1063).
 * Social follow links use SOCIAL_PROFILES (PBI #1173).
 */

import { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Anchor, Group, Stack, Text, UnstyledButton, rem } from '@mantine/core';
import { IconBrandFacebook, IconBrandInstagram } from '@tabler/icons-react';
import { semanticColors } from '../../config/theme';
import { landingContent } from '../../content/landingContent';
import { visiblePublicGuideNavItems } from '../../constants/publicGuideNav';
import { FeaturesContext } from '../../contexts/FeaturesContext';
import { SHOP_NAV_ITEMS } from '../../constants/shopNav';
import {
  SOCIAL_FOLLOW_SECTION_LABEL,
  SOCIAL_PROFILES,
  SocialPlatform,
} from '../../constants/socialProfiles';
import {
  trackShopProductClicked,
  trackSocialFollowClicked,
} from '../../services/marketingTracking';
import { LegalModal, type LegalModalType } from '../common/LegalModal';
import { useConsent } from '../../contexts/ConsentContext';
import { CONSENT_UI_TEXT } from '../../constants/consent';

/** Maps platform keys to Tabler brand icons for the Follow us row. */
const SOCIAL_PLATFORM_ICONS = {
  [SocialPlatform.Instagram]: IconBrandInstagram,
  [SocialPlatform.Facebook]: IconBrandFacebook,
} as const;

/**
 * Renders the shared public footer with Shop links, social follow links,
 * legal links, privacy choices, and copyright.
 */
export const PublicFooter = () => {
  const navigate = useNavigate();
  const isPublicAiPageEnabled = useContext(FeaturesContext)?.isPublicAiPageEnabled ?? false;
  const guideNavItems = visiblePublicGuideNavItems(isPublicAiPageEnabled);
  const [legalModalType, setLegalModalType] = useState<LegalModalType | null>(null);
  const { openPreferences } = useConsent();

  const handleShopClick = (
    path: string,
    productKey: (typeof SHOP_NAV_ITEMS)[number]['key'],
    serviceType: (typeof SHOP_NAV_ITEMS)[number]['serviceType'],
  ) => {
    trackShopProductClicked(productKey, serviceType, 'footer');
    navigate(path);
  };

  return (
    <>
      <Stack gap="xs" c={semanticColors.text.secondary} mb={rem(24)} align="center">
        <Text size="sm" ta="center">
          {landingContent.sections.footer.tagline}
        </Text>
        {landingContent.sections.footer.note ? (
          <Text size="sm" ta="center">
            {landingContent.sections.footer.note}
          </Text>
        ) : null}

        {/* Intent guides — durable nav (PBI #1318) */}
        <Group gap="md" mt="xs" wrap="wrap" justify="center" maw="100%" px="sm">
          {guideNavItems.map((item) => (
            <UnstyledButton
              key={item.key}
              component="button"
              type="button"
              onClick={() => navigate(item.path)}
              style={{ cursor: 'pointer' }}
            >
              <Text size="xs" c={semanticColors.text.secondary} td="underline">
                {item.label}
              </Text>
            </UnstyledButton>
          ))}
        </Group>

        {/* Shop product links — wrap cleanly at narrow widths */}
        <Group gap="md" mt="xs" wrap="wrap" justify="center" maw="100%" px="sm">
          {SHOP_NAV_ITEMS.map((item) => (
            <UnstyledButton
              key={item.key}
              component="button"
              type="button"
              onClick={() => handleShopClick(item.path, item.key, item.serviceType)}
              style={{ cursor: 'pointer' }}
            >
              <Text size="xs" c={semanticColors.text.secondary} td="underline">
                {item.label}
              </Text>
            </UnstyledButton>
          ))}
        </Group>

        {/* Follow us — muted icons, ~44px touch targets (PBI #1173) */}
        <Group gap="xs" mt="xs" wrap="wrap" justify="center" align="center">
          <Text size="xs" c={semanticColors.text.secondary}>
            {SOCIAL_FOLLOW_SECTION_LABEL}
          </Text>
          {SOCIAL_PROFILES.map((profile) => {
            const Icon = SOCIAL_PLATFORM_ICONS[profile.platform];

            return (
              <Anchor
                key={profile.platform}
                href={profile.url}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={profile.ariaLabel}
                c={semanticColors.text.secondary}
                onClick={() => trackSocialFollowClicked(profile.platform, 'footer')}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minWidth: rem(44),
                  minHeight: rem(44),
                }}
              >
                <Icon size={20} aria-hidden />
              </Anchor>
            );
          })}
        </Group>

        {/* Legal and privacy links */}
        <Group gap="md" mt="xs" wrap="wrap" justify="center">
          {landingContent.sections.footer.links.map((link) => {
            const isTerms = link.path === '/terms';
            const isPrivacy = link.path === '/privacy';
            const openModal = isTerms
              ? () => setLegalModalType('terms')
              : isPrivacy
                ? () => setLegalModalType('privacy')
                : null;

            if (openModal) {
              return (
                <UnstyledButton
                  key={link.path}
                  component="button"
                  type="button"
                  onClick={openModal}
                  style={{ cursor: 'pointer' }}
                >
                  <Text size="xs" c={semanticColors.text.secondary} td="underline">
                    {link.label}
                  </Text>
                </UnstyledButton>
              );
            }

            return (
              <Text
                key={link.path}
                component="a"
                href={link.path}
                size="xs"
                c={semanticColors.text.secondary}
                style={{ textDecoration: 'underline', cursor: 'pointer' }}
              >
                {link.label}
              </Text>
            );
          })}
          <Text size="xs" c={semanticColors.text.secondary}>&bull;</Text>
          <UnstyledButton
            component="button"
            type="button"
            onClick={openPreferences}
            style={{ cursor: 'pointer' }}
          >
            <Text size="xs" c={semanticColors.text.secondary} td="underline">
              {CONSENT_UI_TEXT.privacyChoicesLink}
            </Text>
          </UnstyledButton>
        </Group>

        <Text size="xs" c={semanticColors.text.secondary} mt="xs">
          {landingContent.sections.footer.copyright}
        </Text>
      </Stack>

      <LegalModal
        type={legalModalType}
        opened={legalModalType !== null}
        onClose={() => setLegalModalType(null)}
      />
    </>
  );
};
